import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components/macro";
import { Stack, buttonClasses } from "@mui/material";
import { Trans } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import { bottomPadding, isDesktop, topPadding, isApp, isPhone } from "../../tools/device";
import LoginHeader from "./LoginHeader";
import { useSetState } from "components/ReduxProvider";
import Toggler from "components/other/Toggler";
import { usePreLoginQuery } from "./PreLogin";
import MammothNew from "icons/MammothNew";
import { BrandTitle } from "../../components/other/BrandTitle";
import { dispatchEvent, EVENT } from "tools/events";
import { useEffectOnce } from "react-use";
import { ReassuranceScreen } from "./reassurance/ReassuranceScreen";
import { AuthOptions } from "./AuthOptions";
import { LoginMethodEnum } from "../../sharedJs__generated/constants";
import MammothWithBubble, { MammothVariant } from "root/tutorial/onboarding/MammothWithBubble";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  overflow: auto;
  text-align: center;
  padding: ${topPadding}px 0 ${bottomPadding}px;
  .${buttonClasses.outlined} {
    background: white;
  }
`;

const Small = styled.p`
  font-size: ${({ theme }) => theme.duo.fontSize.small};
  line-height: 1.4em;
  color: ${({ theme }) => theme.duo.color.textGrey};
`;

const HeaderSection = styled.div`
  margin-bottom: 20px;
`;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const { data: preLoginData, isLoading: preLoginLoading } = usePreLoginQuery(undefined, params.get("newsletterId"));
  const prefillTypeData = preLoginData?.prefillType as LoginMethodEnum | undefined;
  const prefillEmailData = preLoginData?.prefillEmail;
  const [ignorePrefill, setIgnorePrefill] = useState(false);

  const prefillType = ignorePrefill ? undefined : prefillTypeData;
  const prefillEmail = ignorePrefill ? undefined : prefillEmailData;

  const [isEmailOpen, setIsEmailOpen] = useState(prefillType === LoginMethodEnum.email);
  const [isAuthOpen] = useState(true);
  const [isFromNewsletter, setIsFromNewsletter] = useState(
    !!(params.get("utm_source") === "newsletter" || params.get("newsletterId"))
  );
  const [isSwitchingAccounts] = useState(params.get("switch_account") === "true");
  const hasPrefill = !!prefillType;
  const [areLoginOptionsOpen, setLoginOptionsOpen] = useState(isFromNewsletter || isSwitchingAccounts || hasPrefill);
  const [mammothHeight, setMammothHeight] = useState(120);
  const [subtitleVisible, setSubtitleVisible] = useState(true);
  const setToken = useSetState("token");
  const [showReassurance, setShowReassurance] = useState(!(isFromNewsletter || isSwitchingAccounts || hasPrefill));

  if (preLoginData?.token) {
    setToken(preLoginData.token);
  }

  // Open login options when prefill data arrives
  React.useEffect(() => {
    if (prefillType) {
      setIsFromNewsletter(true);
      setLoginOptionsOpen(true);
      setShowReassurance(false);

      if (prefillType === LoginMethodEnum.email) {
        setIsEmailOpen(true);
        navigate({ search: location.search, hash: "email" }, { replace: true });
      }
    }
  }, [prefillType, navigate, location.search]);

  const toggleEmailMethod = useCallback(
    (show: boolean) => {
      setIsEmailOpen(show);

      // Ignore prefill data when going back
      if (!show && prefillTypeData) {
        setIgnorePrefill(true);
      }

      navigate({ hash: show ? "email" : "" });
    },
    [navigate, prefillTypeData]
  );

  const handleShowAllOptions = useCallback(() => {
    setIgnorePrefill(true);
    navigate({ search: location.search, hash: "" }, { replace: true });
  }, [navigate, location.search]);

  const handleKeyboardToggle = useCallback((isVisible: boolean) => {
    if (isDesktop()) return;
    setSubtitleVisible(!isVisible);
  }, []);

  useEffect(() => {
    const newHeight = isEmailOpen && isPhone() ? 50 : 120;
    if (mammothHeight !== newHeight) {
      setMammothHeight(newHeight);
    }
  }, [isEmailOpen, mammothHeight]);

  useEffect(() => {
    const isEmailHash = location.hash === "#email";
    if (isEmailHash !== isEmailOpen) {
      setIsEmailOpen(isEmailHash);
    }
  }, [location.hash, isEmailOpen]);

  useEffectOnce(() => {
    dispatchEvent(EVENT.ATTConsent);
  });

  if (preLoginLoading) {
    return null;
  }

  if (showReassurance) {
    return (
      <ReassuranceScreen
        onLogin={() => {
          setShowReassurance(false);
          setLoginOptionsOpen(true);
        }}
      />
    );
  }

  return (
    <Wrap>
      <LoginHeader
        onBack={() => {
          setShowReassurance(true);
          setLoginOptionsOpen(false);
          navigate("/");
        }}
        showBack={areLoginOptionsOpen && !isEmailOpen && (isApp() || isAuthOpen)}
      />
      <HeaderSection>
        <Stack alignItems="center" spacing={1}>
          {prefillType && subtitleVisible ? (
            <MammothWithBubble variant={MammothVariant.Large}>
              <Trans>Welcome back!</Trans> <br />
              {prefillType === LoginMethodEnum.email && <Trans>Last time you logged in with your email.</Trans>}
              {prefillType === LoginMethodEnum.google && (
                <Trans>Last time you logged in with your Google account.</Trans>
              )}
              {prefillType === LoginMethodEnum.apple && <Trans>Last time you logged in with your Apple account.</Trans>}
            </MammothWithBubble>
          ) : (
            <MammothNew height={mammothHeight} style={{ transition: "height 0.5s ease", transform: "scaleX(-1)" }} />
          )}
          <BrandTitle>DuoCards</BrandTitle>
          {subtitleVisible && !prefillType && <Trans parent={Small}>Add words from anywhere, never forget them</Trans>}
        </Stack>
      </HeaderSection>
      <Toggler show={isAuthOpen}>
        <AuthOptions
          isEmailOpen={isEmailOpen}
          areLoginOptionsOpen={areLoginOptionsOpen}
          onEmailToggle={toggleEmailMethod}
          onKeyboardToggle={handleKeyboardToggle}
          prefillEmail={prefillEmail || undefined}
          prefillType={prefillType || undefined}
          onShowAllOptions={handleShowAllOptions}
        />
      </Toggler>
      {isDesktop() && <div></div>} {/* empty to do not stick to bottom on desktop */}
    </Wrap>
  );
};

export default LoginPage;
